import type { PaginationState } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import { createParser, useQueryState } from '@frontend/utils/hooks/useQueryState';

/**
 * Hook to manage pagination state in URL search parameters
 *
 * @returns Pagination state and setter compatible with TanStack Table
 */
export function usePaginationSearchParams(): [
    pagination: PaginationState,
    setPagination: (updater: PaginationState | ((state: PaginationState) => PaginationState)) => void,
] {
    const defaultPage = 1;
    const defaultPageSize = 10;
    const defaultPageSizes = [10, 15, 20, 25, 50, 100] as const;

    type ValidPageSize = (typeof defaultPageSizes)[number];

    const pageParser = createParser<number>({
        parse: (value: string | null) => {
            if (!value) {
                return undefined;
            }
            const parsed = parseInt(value, 10);

            return isNaN(parsed) ? undefined : Math.max(defaultPage, parsed);
        },
        serialize: (value: number | undefined) => (value !== undefined && value > defaultPage ? String(value) : null),
    });

    const pageSizeParser = createParser<ValidPageSize>({
        parse: (value: string | null) => {
            if (!value) {
                return undefined;
            }
            const parsed = parseInt(value, 10);

            return isNaN(parsed) || !defaultPageSizes.includes(parsed as ValidPageSize)
                ? undefined
                : (parsed as ValidPageSize);
        },
        serialize: (value: ValidPageSize | undefined) =>
            value !== undefined && value !== defaultPageSize ? String(value) : null,
    });

    const [page, setPage] = useQueryState('page', pageParser.withDefault(defaultPage));
    const [pageSize, setPageSize] = useQueryState('pageSize', pageSizeParser.withDefault(defaultPageSize));

    const pagination = useMemo(
        () => ({
            pageIndex: page - 1,
            pageSize,
        }),
        [page, pageSize]
    );

    const setPagination = useCallback(
        (updater: PaginationState | ((state: PaginationState) => PaginationState)) => {
            if (typeof updater === 'function') {
                const currentState = {
                    pageIndex: page - 1,
                    pageSize,
                };
                const newState = updater(currentState);
                setPage(newState.pageIndex + 1);
                setPageSize(newState.pageSize as ValidPageSize);
            } else {
                setPage(updater.pageIndex + 1);
                setPageSize(updater.pageSize as ValidPageSize);
            }
        },
        [setPage, setPageSize, page, pageSize]
    );

    return [pagination, setPagination];
}
